import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useFormContext } from 'react-hook-form';
import { Field } from '../../../auth/field';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { CreateCurriculumSubheader } from '../../../create-curriculum/create-curriculum-subheader';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import { useQuery } from '@tanstack/react-query';
import { ProfileAddVideo } from '../../../profile/profile-add-video';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';

type AddOfferStep0Props = {
  categories: any[];
  employmentTypes: any[];
  isEdit?: boolean;
};

export const AddOfferStep0 = ({ categories, employmentTypes, isEdit }: AddOfferStep0Props) => {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const form = useFormContext();
  const errors = form.formState.errors;
  const me = useCustomerStore(s => s.me);

  const isDualEducation = form.watch('dual_education');

  const filteredEmploymentTypes = !isDualEducation
    ? employmentTypes
    : employmentTypes.filter(type => type.value > 2);

  const { data: countries } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-6 bg-white p-6 md:gap-8 md:rounded-lg md:p-8">
        <h1 className="text-2xl font-bold md:text-4xl">{t('text861')}</h1>
        <Field
          name="name"
          label={t('text956')}
          placeholder={t('text956') + '...'}
          required
          control={form.control}
        />
        {(me?.customer?.tags?.some(tag => tag.id === 4) || (isEdit && isDualEducation)) && (
          <div className="flex flex-col gap-3">
            <Field
              name="dual_education"
              label={t('Duálne vzdelávanie')}
              type="checkbox"
              control={form.control}
            />
            <p className="text-sm text-muted-foreground">
              {t(
                'Označte iba ponuku duálneho vzdelávania. Oprávnenie firmy na duál ani profesie v profile firmy neurčujú typ tejto ponuky.',
              )}
            </p>
          </div>
        )}
        <Field
          name="categories"
          label={t('text176') + ' (max. 3)'}
          placeholder={t('text1410')}
          type="multiselect"
          options={categories}
          required
          control={form.control}
          maxSelection={3}
        />
        <Field
          name="city"
          label={t('text1514')}
          placeholder={t('text39')}
          required
          control={form.control}
        />
        {isEdit && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field
              name="company_name"
              label={t('text1432')}
              placeholder={t('text1432') + '...'}
              required
              control={form.control}
            />
            <Field
              name="address"
              label={t('text38')}
              placeholder={t('text38') + '...'}
              required
              control={form.control}
            />
            <Field
              name="street_number"
              label={t('text485')}
              placeholder={t('text485') + '...'}
              control={form.control}
            />
            <Field
              name="postcode"
              label={t('text40')}
              placeholder={t('text40') + '...'}
              required
              control={form.control}
            />
          </div>
        )}
        <Field
          control={form.control}
          name="country"
          type="select"
          required
          options={countries?.data.map(c => ({ value: c.id, label: c.name })) || []}
          label={t('text41')}
        />
        <ProfileAddVideo
          type="all_perms"
          customDescription="Pridajte video ohľadom Vašej pracovnej ponuky"
        />
        {!isDualEducation && (
          <Field
            name="employment_types"
            label={t('text1733')}
            options={filteredEmploymentTypes}
            type="radio"
            required
            control={form.control}
            className={{
              radioGroupItem: 'h-5 w-5 border border-border',
              wrapper: 'gap-5',
            }}
          />
        )}
        {!isDualEducation && (
          <div className="flex flex-col gap-3">
            <Field
              name="for_graduates"
              label={t('text2119')}
              type="checkbox"
              control={form.control}
            />
          </div>
        )}
        {!isDualEducation && (
          <div className="flex flex-col gap-3">
            <Field
              name="for_foreigners"
              label={t('text2118')}
              type="checkbox"
              control={form.control}
            />
          </div>
        )}
        {!isDualEducation && (
          <div className="flex flex-col gap-3">
            <h2 className="pl-2 text-sm font-medium md:text-base">{t('text1766')}</h2>
            <Field name="homeoffice" label={t('text1417')} type="checkbox" control={form.control} />
          </div>
        )}
        {!isDualEducation && (
          <>
            <Separator />
            <div className="flex flex-col gap-2">
              <CreateCurriculumSubheader title={t('text1527')} required />
              <div className="grid grid-cols-2 gap-4">
                <Field
                  name="min_salary"
                  label={t('Od')}
                  placeholder={t('Od')}
                  required
                  control={form.control}
                />
                <Field
                  name="max_salary"
                  label={t('Do')}
                  placeholder={t('Do')}
                  control={form.control}
                />
              </div>
            </div>
          </>
        )}
        <Separator />
        <CreateCurriculumSubheader
          title={t('text1767')}
          required
          className={errors.date_start && 'text-destructive'}
        />
        <div className="flex flex-col gap-4">
          <Field
            name="date_start"
            placeholder={t('text1589')}
            type="date"
            required={isEdit}
            control={form.control}
          />
          <div className="flex flex-col gap-3">
            <Field
              name="start_immediately"
              label="Začať ihneď"
              type="checkbox"
              control={form.control}
            />
          </div>
        </div>
        <Separator />
        <CreateCurriculumSubheader
          title={t('text1768')}
          required
          className={errors.description && 'text-destructive'}
        />
        <Field name="description" required type="textarea" control={form.control} />
        <CreateCurriculumSubheader
          title={t('text2117')}
          className={errors.benefit && 'text-destructive'}
        />
        <Field name="benefit" required type="textarea" control={form.control} />
      </div>
      <div className="flex flex-col gap-6 bg-white p-6 md:gap-8 md:rounded-lg md:p-8">
        <h1 className="text-2xl font-bold md:text-4xl">{t('text803')}</h1>
        <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 md:gap-4">
          <Field
            name="contact_name"
            label={t('text33')}
            placeholder={t('text33')}
            required
            control={form.control}
          />
          <Field
            name="contact_surname"
            label={t('text34')}
            placeholder={t('text34')}
            required
            control={form.control}
          />
          <Field
            name="contact_email"
            label={t('text35')}
            placeholder={t('text35')}
            required
            control={form.control}
          />
          <Field
            name="contact_phone"
            label={t('text36')}
            placeholder={t('text36')}
            type="tel"
            required
            control={form.control}
          />
        </div>
      </div>
    </div>
  );
};
